/**
 * Screen 01 — Signup · Role selection.
 *
 * Centred two-card layout. Picking a card kicks off Firebase Google
 * sign-in with that role pre-recorded; first-time users land at
 * IFAVerify (IFA) or ApprovalPending (Individual). Returning users
 * land at their workspace home (router decides).
 *
 * Per locked behavioural change: both role cards show "Approval
 * required" — no timeline promises.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppNav } from "@/components/AppNav";
import { RoleCard } from "@/components/RoleCard";
import { useAuth } from "@/auth";
import { ApiError } from "@/lib/api";
import { Routes, homeFor } from "@/lib/routes";
import type { UserRole } from "@/types";
import { track } from "@/lib/track";

export function SignUp() {
  const nav = useNavigate();
  const { signInWithGoogle } = useAuth();
  const [busy, setBusy] = useState<UserRole | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePick = async (role: UserRole) => {
    if (busy) return;
    track("role_selected", { role });
    setBusy(role);
    setError(null);
    try {
      const profile = await signInWithGoogle(role);
      // Returning active user → workspace home; first-time IFA →
      // verify; first-time Individual or pending IFA → pending.
      if (profile.status === "active") {
        nav(homeFor(profile.role), { replace: true });
      } else if (
        (profile.role === "ifa" || profile.role === "mfd" || profile.role === "ra") &&
        !profile.sebiRegistration
      ) {
        nav(Routes.IFAVerify, { replace: true });
      } else {
        nav(Routes.Pending, { replace: true });
      }
    } catch (e: any) {
      console.error("[signup] auth failed:", e);
      setError(
        e?.code === "auth/popup-closed-by-user"
          ? "Sign-in popup was closed before you finished."
          : e instanceof ApiError && e.status === 409
            // ROLE_LOCKED: an approved adviser workspace; the server's message
            // says to contact support rather than "sign-in failed".
            ? e.message
            : "Sign-in failed. Try again, or refresh the page.",
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="col" style={{ minHeight: "100%", background: "var(--paper-2)" }}>
      <AppNav mode="signup" />

      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 48px 56px",
        }}
      >
        <div style={{ maxWidth: 1180, width: "100%" }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div className="kicker">
              <span className="sq" />
              Account setup · one step
            </div>
            <h1 className="h-display" style={{ marginTop: 12, fontSize: 36 }}>
              How will you use StockSense&nbsp;AI?
            </h1>
            <p
              className="muted"
              style={{
                marginTop: 10,
                fontSize: 15,
                maxWidth: "52ch",
                marginInline: "auto",
                lineHeight: 1.55,
              }}
            >
              {/* There is no preview: handlePick opens the Google popup on the
                  first click. Promising one and then not showing it is a
                  surprise at the exact step people abandon. Say what actually
                  happens, and remove the fear of choosing wrong. */}
              We tailor the workspace to who you are. Pick a path — you can
              change it later, and nothing you have saved is lost.
            </p>
          </div>

          <div
            // auto-fit + min(): 3-across on desktop, stacked on a phone. A hard
            // repeat(3, 1fr) crushed the three role cards to 79px each at 320px
            // and pushed the third past the edge — and being an inline style, no
            // stylesheet media query could reach it.
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 16 }}
            className="grid-3"
          >
            <RoleCard kind="individual" onPick={handlePick} />
            <RoleCard kind="mfd" onPick={handlePick} />
            <RoleCard kind="ifa" onPick={handlePick} />
            <RoleCard kind="ra" onPick={handlePick} />
          </div>

          {busy && (
            <p
              className="muted"
              style={{
                marginTop: 16,
                textAlign: "center",
                fontSize: 12,
                fontFamily: "var(--mono)",
                letterSpacing: ".06em",
              }}
            >
              Opening Google sign-in…
            </p>
          )}
          {error && (
            <p
              className="text-loss"
              style={{
                marginTop: 16,
                textAlign: "center",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {error}
            </p>
          )}

          <p
            style={{
              marginTop: 22,
              textAlign: "center",
              fontSize: 11.5,
              color: "var(--ink-light)",
              lineHeight: 1.55,
            }}
          >
            By continuing you accept the{" "}
            <a href="/terms.html" target="_blank" rel="noopener noreferrer"
               style={{ color: "var(--ink-mid)", textDecoration: "underline" }}>
              Terms
            </a>{" "}
            and{" "}
            <a href="/terms.html#disclaimer" target="_blank" rel="noopener noreferrer"
               style={{ color: "var(--ink-mid)", textDecoration: "underline" }}>
              Disclaimer
            </a>
            .
          </p>
          <div
            style={{
              marginTop: 16,
              textAlign: "center",
              fontSize: 11,
              color: "var(--ink-light)",
              fontFamily: "var(--mono)",
              letterSpacing: ".10em",
            }}
          >
            Educational technology tool — not a SEBI-registered RA or IA.
          </div>
          <div style={{ textAlign: "center", marginTop: 8, fontSize: 11, opacity: 0.7 }}>
            <a href="/terms.html" style={{ color: "inherit" }}>Terms</a> ·{" "}
            <a href="/privacy.html" style={{ color: "inherit" }}>Privacy</a> ·{" "}
            <a href="/refund.html" style={{ color: "inherit" }}>Refund &amp; Cancellation</a>
          </div>
        </div>
      </main>
    </div>
  );
}
